import { useState, useEffect } from 'react';
import { getTokenForTenant } from '@web-config-app/api';
import { decodeJwtPayload } from '../../utils/decode-jwt-payload';
import { getTenantConfig } from '../../utils/get-tenant-config';

type TenantScopedTokenClaims = {
  'https://el/accessible_tenants'?: string[];
};

export type TenantScopedTokenClaimsResult = {
  accessibleTenantIds: string[];
  isLoading: boolean;
  error: Error | null;
};

/**
 * Acquires a tenant-scoped token via `getTokenForTenant` and decodes its JWT
 * payload to extract `tenant_id` and `accessible_tenants` claims.
 *
 * Unlike `useDiscoveryTokenClaims` (which fetches the discovery token before a
 * tenant is selected), this hook fetches a token scoped to a specific tenant's
 * organization and audience. Auth0 caches tokens per (audience, organization)
 * combination, so subsequent calls return instantly from cache until expiry.
 *
 *
 * @param tenantId - The tenant whose scoped token to fetch
 * @param enabled - Only fetches when `true`
 */
export const useTenantScopedTokenClaims = (
  tenantId: string | undefined,
  enabled: boolean,
): TenantScopedTokenClaimsResult => {
  const [accessibleTenantIds, setAccessibleTenantIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!enabled || !tenantId) {
      setAccessibleTenantIds([]);
      setError(null);
      setIsLoading(false);
      return () => {};
    }

    let cancelled = false;

    const fetchToken = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const tenantConfig = getTenantConfig(tenantId);
        if (!tenantConfig) {
          throw new Error(`Unknown tenant ID: ${tenantId}`);
        }

        const { auth } = tenantConfig.core;
        if (!('clientOptions' in auth)) {
          throw new Error(
            `Tenant "${tenantId}" has no client options configured.`,
          );
        }

        const { audience, organization } = auth.clientOptions;
        if (!audience || !organization) {
          throw new Error(
            `Tenant "${tenantId}" is missing required auth configuration (audience or organization).`,
          );
        }

        const token = await getTokenForTenant(audience, organization);
        if (cancelled) return;

        const claims = decodeJwtPayload<TenantScopedTokenClaims>(token);
        setAccessibleTenantIds(claims['https://el/accessible_tenants'] ?? []);
      } catch (err) {
        if (cancelled) return;

        setError(
          err instanceof Error
            ? err
            : new Error('Failed to fetch tenant-scoped token.'),
        );
        setAccessibleTenantIds([]);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchToken();

    return () => {
      cancelled = true;
    };
  }, [tenantId, enabled]);

  return {
    accessibleTenantIds,
    isLoading,
    error,
  };
};
